interface MoodEnergyIndicatorProps {
  mood?: number;
  energy?: number;
  morningMood?: number;
  morningEnergy?: number;
  eveningMood?: number;
  eveningEnergy?: number;
}

const clamp = (value: number): number => Math.min(10, Math.max(1, Math.round(value)));

const moodLabel = (value: number): string => {
  if (value <= 3) return 'Low';
  if (value <= 5) return 'Steady';
  if (value <= 7) return 'Good';
  return 'Great';
};

const energyLabel = (value: number): string => {
  if (value <= 3) return 'Drained';
  if (value <= 5) return 'Low';
  if (value <= 7) return 'Balanced';
  return 'High';
};

const formatDelta = (value: number): string => {
  if (value > 0) return `+${value}`;
  return `${value}`;
};

export default function MoodEnergyIndicator(props: MoodEnergyIndicatorProps) {
  const mood = toValue(props.eveningMood ?? props.morningMood ?? props.mood);
  const energy = toValue(props.eveningEnergy ?? props.morningEnergy ?? props.energy);
  const moodMorning = toValue(props.morningMood);
  const moodEvening = toValue(props.eveningMood);
  const energyMorning = toValue(props.morningEnergy);
  const energyEvening = toValue(props.eveningEnergy);

  if (!mood && !energy) return null;

  return (
    <section className="flank-card rounded-xl p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Mood and energy</h3>
        {(moodMorning && moodEvening) || (energyMorning && energyEvening) ? (
          <span className="text-[11px] text-[var(--color-text-muted)]">Morning to evening</span>
        ) : null}
      </div>

      <div className="mt-3 flex flex-col gap-3">
        {mood ? (
          <MetricRail
            label="Mood"
            value={mood}
            descriptor={moodLabel(mood)}
            gradient="linear-gradient(90deg, #FF6B35 0%, #FFD120 50%, #4BD137 100%)"
            delta={moodMorning && moodEvening ? moodEvening - moodMorning : undefined}
          />
        ) : null}

        {energy ? (
          <MetricRail
            label="Energy"
            value={energy}
            descriptor={energyLabel(energy)}
            gradient="linear-gradient(90deg, #FF6B35 0%, #FFD120 50%, #4BD137 100%)"
            delta={energyMorning && energyEvening ? energyEvening - energyMorning : undefined}
          />
        ) : null}
      </div>
    </section>
  );
}

interface MetricRailProps {
  label: string;
  value: number;
  descriptor: string;
  gradient: string;
  delta?: number;
}

function MetricRail({ label, value, descriptor, gradient, delta }: MetricRailProps) {
  const percent = ((value - 1) / 9) * 100;

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-[var(--color-text-secondary)]">{label}</p>
        <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
          {delta !== undefined ? (
            <span
              style={{
                color: delta > 0
                  ? 'var(--color-success)'
                  : delta < 0
                    ? 'var(--color-warning)'
                    : 'var(--color-text-muted)',
              }}
            >
              {formatDelta(delta)}
            </span>
          ) : null}
          <span className="font-semibold text-[var(--color-text-primary)]">{value}</span>
          <span>{descriptor}</span>
        </div>
      </div>

      <div className="relative mt-2 h-2 rounded-full overflow-hidden">
        <div className="absolute inset-0 rounded-full" style={{ background: gradient, opacity: 0.28 }} />
        {percent > 0 && (
          <div
            className="absolute left-0 top-0 h-full rounded-full"
            style={{
              width: `${percent}%`,
              background: gradient,
              backgroundSize: `${10000 / percent}% 100%`,
            }}
          />
        )}
      </div>
    </div>
  );
}

function toValue(value: number | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined;
  return clamp(value);
}
